import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useGroup } from '../contexts/GroupContext';
import { useSavedViews } from '../contexts/SavedViewsContext';
import { useAuth } from '../contexts/AuthContext';
import NodeDetailPanel from '../components/NodeDetailPanel';
import AddRelationshipModal from '../components/AddRelationshipModal';
import CreateNodeModal from '../components/CreateNodeModal';
import DatabaseTableView from './graph/DatabaseTableView';
import NodeComposer from './graph/NodeComposer';
import { upsertPage, removePage } from '../utils/composerPages';
import { setAppPreview } from '../utils/appPreview';
import { useResizablePanel } from '@ofself/shell';
import useGraphData from './graph/useGraphData';
import useGraphFilters from './graph/useGraphFilters';
import useNodeCRUD from './graph/useNodeCRUD';
import DevSchemasTab from './dashboard/DevSchemasTab';
import ActionInboxPage from './ActionInboxPage';
import api from '../services/api';
import { whenOf } from '../nucleus/format';
import '../styles/GraphView.css';

// 50, not 300 (owner call): a page of 300 is 300 rows over the wire and 300
// tiles laid out on arrival — the block view builds a Plate and a ledger for
// each, so the cost is paid in render as well as in transfer. The footer's
// pager already walks pages; it just had almost nothing to walk.
const PAGE_SIZE = 50;

function GraphView({ forcedTab = null }) {
    const { activeGroup, isGroupContext, switchToGroup, switchToPersonal } = useGroup();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { groupId: urlGroupId, appId: catalogAppId } = useParams();
    const catalogMode = !!catalogAppId;
    // Sync group context from URL: /group/:groupId/graph sets group, /graph sets personal
    useEffect(() => {
        if (urlGroupId && activeGroup?.id !== urlGroupId) {
            switchToGroup(urlGroupId);
        } else if (!urlGroupId && isGroupContext) {
            // On /graph (no group prefix) — stay in whatever context is active
        }
    }, [urlGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

    // /app/:appId/graph is neither a group-bearing path nor /graph itself, so
    // the effect above never resets a stale active group — arriving here from
    // inside a group would otherwise silently keep useGraphData routed into
    // the GROUP fetch branch instead of the catalog one.
    useEffect(() => {
        if (catalogMode && isGroupContext) switchToPersonal();
    }, [catalogMode, isGroupContext, switchToPersonal]);

    // Selecting a node writes its id back into the URL for deep-linking (see
    // the state->URL effect below) — without the catalog arm here, that sync
    // silently bounced every node click on /app/:appId/graph back to the
    // personal /graph route instead of staying put.
    const graphBasePath = urlGroupId ? `/group/${urlGroupId}/graph`
        : catalogAppId ? `/app/${catalogAppId}/graph`
        : '/graph';

    // THE TAB CAN COME FROM THE PATH. A route like /schemas passes it as a
    // prop; everything else still reads ?tab=, defaulting to the database.
    const graphTab = forcedTab || searchParams.get('tab') || 'database';


    const [access, setAccess] = useState('owned');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState('updated_at');
    const [order, setOrder] = useState('desc');

    const filters = useGraphFilters();

    // --- Saved views bridge ---
    const savedViews = useSavedViews();
    // Publish a snapshot of the current filter state so "New view" can capture it.
    useEffect(() => {
        savedViews.registerCapture(() => ({
            graphSearch: filters.graphSearch,
            schemas: [...filters.selectedSchemas],
            apps: [...filters.selectedApps],
            rels: [...filters.selectedRelationshipTypes],
            dateField: filters.dateField,
            dateAfter: filters.dateAfter,
            dateBefore: filters.dateBefore,
            access, sort, order,
        }));
    });
    // Apply a view when one is selected from the sidebar.
    const appliedViewNonce = useRef(0);
    useEffect(() => {
        const p = savedViews.pending;
        if (!p || p.nonce === appliedViewNonce.current) return;
        appliedViewNonce.current = p.nonce;
        const f = p.filters || {};
        filters.setGraphSearch(f.graphSearch || '');
        filters.setSelectedSchemas(new Set(f.schemas || []));
        filters.setSelectedApps(new Set(f.apps || []));
        filters.setSelectedRelationshipTypes(new Set(f.rels || []));
        filters.setDateField(f.dateField || 'created_at');
        filters.setDateAfter(f.dateAfter || '');
        filters.setDateBefore(f.dateBefore || '');
        setAccess(f.access || 'owned');
        setSort(f.sort || 'updated_at');
        setOrder(f.order || 'desc');
        setPage(0);
        // Consume it — otherwise this snapshot re-applies on every remount and
        // can leave All Nodes stuck on a filter that matches nothing.
        savedViews.clearPending();
    }, [savedViews.pending]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset to page 0 whenever filters or access change
    useEffect(() => {
        setPage(0);
    }, [filters.graphSearch, filters.selectedSchemas,
        filters.selectedRelationshipTypes, filters.selectedApps,
        filters.valueJsonField, filters.valueJsonValue,
        filters.valueJsonMin, filters.valueJsonMax, filters.dateAfter, filters.dateBefore, filters.selectedStrata, access]);

    const data = useGraphData({
        graphTab,
        access,
        page,
        pageSize: PAGE_SIZE,
        filters: { ...filters.filterParams, sort, order },
        catalogAppId,
    });

    // Scopes this TAB to previewing this one app — ProtectedRoute (App.js)
    // and useNavItems (Layout.js) both read this same flag to hard-block
    // every other route and reduce the nav to Graph alone. Set as soon as
    // catalogMode is known (no need to wait for the name — the guard only
    // cares about the id) and refined once the catalog fetch resolves it.
    useEffect(() => {
        if (catalogMode) setAppPreview(catalogAppId, data.catalogAppName);
    }, [catalogMode, catalogAppId, data.catalogAppName]);

    // The node-detail seam runs on the shared shell kit (same hook as the
    // sidebar). edge:'left' → the overlay grows as you drag its left seam.
    const { width: detailWidth, separatorProps: detailResizeProps } = useResizablePanel({
        edge: 'left', initial: 520, min: 280, max: 700, storageKey: 'explore-detail-w',
    });
    const detailNodeRef = useRef(null);

    const crud = useNodeCRUD({
        graphTab,
        relationships: data.relationships,
        loadIdentityGraph: data.loadIdentityGraph,
        catalogMode,
    });

    // Close = let the kit overlay EASE shut (--panel-w → 0); no bespoke
    // keyframe/timeout — the panel stays mounted, width does the motion.
    const handleDetailClose = useCallback(() => {
        crud.setSelectedNode(null);
    }, [crud]);

    // Keep ref to last selected node for close animation
    useEffect(() => {
        if (crud.selectedNode) detailNodeRef.current = crud.selectedNode;
    }, [crud.selectedNode]);

    // Clear selection and reset page when switching between personal/group context
    useEffect(() => {
        crud.setSelectedNode(null);
        setPage(0);
    }, [isGroupContext, activeGroup?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- URL ↔ State sync ---
    const initializedFromUrl = useRef(false);
    // Capture node ID at render time before any effect can overwrite the URL
    const initialNodeId = useRef(searchParams.get('node'));

    // On mount: restore state from URL params
    useEffect(() => {
        if (initializedFromUrl.current) return;
        initializedFromUrl.current = true;

        const search = searchParams.get('search');
        if (search) filters.setGraphSearch(search);

        const nodeId = initialNodeId.current;
        if (nodeId && data.nodes.length > 0) {
            const found = data.nodes.find(n => n.id === nodeId);
            if (found) {
                initialNodeId.current = null;
                crud.setSelectedNode(found);
            }
        }

        const schemaIds = searchParams.get('schemas');
        if (schemaIds) {
            const ids = schemaIds.split(',').filter(Boolean);
            if (ids.length > 0) filters.setSelectedSchemas(new Set(ids));
        }
    }, [data.nodes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Apply the `apps` URL filter (the sidebar Apps drawer) on mount and whenever
    // it changes — lets app items act as glorified app filters into All Nodes.
    const appsParam = searchParams.get('apps');
    useEffect(() => {
        if (appsParam == null) return;
        filters.setSelectedApps(new Set(appsParam.split(',').filter(Boolean)));
    }, [appsParam]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sidebar compose → dedicated full-page node composer (distinct from the
    // database "New" button, which keeps the modal). The node is created
    // immediately so there's no Save button; the composer autosaves. `compose`
    // can be 'node' (create new) or an existing node id (reopen a page).
    const [composeNode, setComposeNode] = useState(null);
    const composeBusy = useRef(false);
    useEffect(() => {
        const c = searchParams.get('compose');
        if (!c || composeBusy.current) return;
        composeBusy.current = true;
        const next = new URLSearchParams(searchParams);
        next.delete('compose');
        setSearchParams(next, { replace: true });
        (async () => {
            try {
                if (c === 'node') {
                    const resp = await api.createNode('New node', {});
                    const node = resp.node || resp;
                    upsertPage(user?.id, { id: node.id, title: node.title || 'New node' });
                    window.dispatchEvent(new CustomEvent('nodes-changed'));
                    setComposeNode(node);
                } else {
                    const resp = await api.getNode(c);
                    setComposeNode(resp.node || resp);
                }
            } catch { /* ignore */ } finally { composeBusy.current = false; }
        })();
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // Leaving the composer: drop a brand-new node that was never touched
    // (still the default title, no content/schema) so empty pages don't pile up.
    const exitComposer = useCallback(async () => {
        const n = composeNode;
        setComposeNode(null);
        if (!n) return;
        const untouched = (!n.title || n.title === 'New node') && !n.value_json?.content && !n.schema_id;
        if (untouched) {
            try {
                const fresh = await api.getNode(n.id).then(r => r.node || r).catch(() => null);
                const stillEmpty = fresh && (!fresh.title || fresh.title === 'New node') && !fresh.value_json?.content && !fresh.schema_id;
                if (stillEmpty) { await api.deleteNode(n.id); removePage(user?.id, n.id); }
            } catch { /* ignore */ }
        }
        window.dispatchEvent(new CustomEvent('nodes-changed'));
        data.reload?.();
    }, [composeNode, user?.id, data]);

    // Leave the composer if the user navigates to another database/library.
    useEffect(() => { if (composeNode) exitComposer(); }, [savedViews.activeViewId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Leave the composer when the sidebar's Node library link is clicked. The
    // active view may already be null (we're composing from the master library),
    // so the activeViewId effect above won't fire — listen for an explicit signal.
    useEffect(() => {
        const onExit = () => { if (composeNode) exitComposer(); };
        window.addEventListener('exit-composer', onExit);
        return () => window.removeEventListener('exit-composer', onExit);
    }, [composeNode, exitComposer]);

    // Retry node selection once data loads (URL may arrive before nodes).
    // If not on the current page, fetch it directly by ID.
    // Clear initialNodeId once resolved so the URL write effect stops pinning it.
    const nodeFetchAttempted = useRef(false);
    useEffect(() => {
        if (!initializedFromUrl.current) return;
        const nodeId = initialNodeId.current;
        if (!nodeId || crud.selectedNode) return;
        if (data.nodes.length > 0) {
            const found = data.nodes.find(n => n.id === nodeId);
            if (found) {
                initialNodeId.current = null;
                crud.setSelectedNode(found);
                return;
            }
            // Node not on current page — fetch directly once, then search by title so it appears in the list
            if (!nodeFetchAttempted.current) {
                nodeFetchAttempted.current = true;
                api.getNode(nodeId)
                    .then(resp => {
                        const node = resp.node || resp;
                        if (node?.id) {
                            initialNodeId.current = null;
                            // Search by title so the node appears in the paginated list and gets highlighted
                            if (node.title) filters.setGraphSearch(node.title);
                            crud.setSelectedNode(node);
                        }
                    })
                    .catch(() => { initialNodeId.current = null; });
            }
        }
    }, [data.nodes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Write state → URL (debounced via replace)
    useEffect(() => {
        if (!initializedFromUrl.current) return;
        const params = new URLSearchParams();
        // THE DEFAULT IS NOT WORTH SAYING: ?tab=database is what /graph already
        // means (graphTab falls back to it when absent), so stamping it only
        // made every visit to the graph wear a parameter that changed nothing.
        // Old links carrying it still read fine — the reader is unchanged.
        if (!forcedTab && graphTab !== 'database') params.set('tab', graphTab);

        if (filters.graphSearch) params.set('search', filters.graphSearch);
        // Preserve pending URL node until it resolves to a selectedNode
        const nodeToWrite = crud.selectedNode?.id || initialNodeId.current;
        if (nodeToWrite) params.set('node', nodeToWrite);
        if (filters.selectedSchemas.size > 0) params.set('schemas', [...filters.selectedSchemas].join(','));

        const newSearch = params.toString();
        const currentSearch = window.location.search.replace(/^\?/, '');
        if (newSearch !== currentSearch) {
            // On a path-based tab (/schemas) the base path is NOT /graph —
            // navigating there would throw you off the route you just opened.
            navigate(`${forcedTab ? window.location.pathname : graphBasePath}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        }
    }, [graphTab, forcedTab, crud.selectedNode?.id, filters.graphSearch, filters.selectedSchemas, navigate, graphBasePath]);

    return (
        <div className="graph-view">
            {data.isTimeTravelMode && (
                <div className="graph-controls">
                    <div className="time-travel-controls">
                        <span>Viewing graph as of {whenOf(data.timestamp, { year: true })}</span>
                        <button onClick={() => navigate(graphBasePath)} className="btn btn-return">
                            Return to Current
                        </button>
                    </div>
                </div>
            )}

            {/* Schemas tab */}
            {graphTab === 'schemas' && <DevSchemasTab />}

            {/* Action Inbox — Proposals + Questions combined, matching the HTML mockup */}
            {(graphTab === 'proposals' || graphTab === 'questions') && (
                <ActionInboxPage initialView={graphTab === 'questions' ? 'questions' : 'proposals'} />
            )}

            {/* Sidebar compose → dedicated Notion-style node composer (full content area) */}
            {graphTab === 'database' && composeNode && (
                <NodeComposer
                    key={composeNode.id}
                    node={composeNode}
                    uid={user?.id}
                    onLeave={exitComposer}
                />
            )}

            {/* Explore tab */}
            {graphTab === 'database' && !composeNode && (
                <div className="explore-layout blocks">
                    <div className="explore-layout__table">
                        <DatabaseTableView
                            dbName={(() => {
                                const av = savedViews.views.find(v => v.id === savedViews.activeViewId);
                                // The master library carries no heading — the
                                // sidebar already says where you are, and
                                // "Node library" over every visit repeated it.
                                // Only a NAMED view titles the table.
                                if (!av) return null;
                                // A child view keeps showing its database's name as the title.
                                if (av.settings?.parentDatabaseId) return savedViews.views.find(v => v.id === av.settings.parentDatabaseId)?.name || av.name;
                                return av.name;
                            })()}
                            nodes={data.nodes}
                            total={data.total}
                            page={page}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPage}
                            sort={sort}
                            order={order}
                            onSortChange={(v) => { setSort(v); setPage(0); }}
                            onOrderChange={(v) => { setOrder(v); setPage(0); }}
                            selectedNodeId={crud.selectedNode?.id || null}
                            pendingNodeId={initialNodeId.current}
                            onNodeSelect={crud.handleNodeClick}
                            schemasMap={data.schemasMap}
                            loading={data.loading}
                            onCreateNode={() => crud.setShowCreateModal(true)}
                            onManageSchemas={urlGroupId ? () => navigate(`/group/${urlGroupId}/schemas`) : undefined}
                            onManageApps={() => navigate('/manage?tab=apps')}
                            access={access}
                            setAccess={setAccess}
                            graphSearch={filters.graphSearch}
                            setGraphSearch={filters.setGraphSearch}
                            schemaFilters={data.schemaFilters}
                            selectedSchemas={filters.selectedSchemas}
                            toggleSchema={filters.toggleSchema}
                            deselectAllSchemas={filters.deselectAllSchemas}
                            relationshipTypes={data.relationshipTypes}
                            relationships={data.relationships}
                            selectedRelationshipTypes={filters.selectedRelationshipTypes}
                            toggleRelationshipType={filters.toggleRelationshipType}
                            deselectAllRelTypes={filters.deselectAllRelTypes}
                            selectedApps={filters.selectedApps}
                            toggleApp={filters.toggleApp}
                            deselectAllApps={filters.deselectAllApps}
                            selectedStrata={filters.selectedStrata}
                            toggleStratum={filters.toggleStratum}
                            deselectAllStrata={filters.deselectAllStrata}
                            facets={data.facets}
                            facetsLoading={data.facetsLoading}
                            valueJsonField={filters.valueJsonField}
                            setValueJsonField={filters.setValueJsonField}
                            valueJsonValue={filters.valueJsonValue}
                            setValueJsonValue={filters.setValueJsonValue}
                            valueJsonMin={filters.valueJsonMin}
                            setValueJsonMin={filters.setValueJsonMin}
                            valueJsonMax={filters.valueJsonMax}
                            setValueJsonMax={filters.setValueJsonMax}
                            valueJsonMatchMode={filters.valueJsonMatchMode}
                            setValueJsonMatchMode={filters.setValueJsonMatchMode}
                            dateField={filters.dateField}
                            setDateField={filters.setDateField}
                            dateAfter={filters.dateAfter}
                            setDateAfter={filters.setDateAfter}
                            dateBefore={filters.dateBefore}
                            setDateBefore={filters.setDateBefore}
                            catalogMode={catalogMode}
                        />
                    </div>
                    {/* Node detail — a first-class OVERLAY panel that opens/closes by
                        EASING ITS WIDTH (the shell's intrinsic motion): --panel-w goes
                        0 ↔ detailWidth and the .block width transition slides it in
                        and out. Always mounted so the ease runs both ways. */}
                    <div
                        className="block block--overlay explore-layout__detail"
                        style={{ '--panel-w': crud.selectedNode ? `${detailWidth}px` : '0px' }}
                        aria-hidden={!crud.selectedNode}
                    >
                        {/* the seam IS the handle — the shell's invisible hit-zone on the overlay's left edge */}
                        <div className="block__resize" {...detailResizeProps} />
                        {(crud.selectedNode || detailNodeRef.current) && (
                            <NodeDetailPanel
                                    node={crud.selectedNode || detailNodeRef.current}
                                    nodes={data.nodes}
                                    relationships={data.relationships}
                                    schemasMap={data.schemasMap}
                                    onClose={handleDetailClose}
                                    readOnly={catalogMode}
                                    onUpdateNode={catalogMode ? undefined : crud.handleUpdateNode}
                                    onDeleteNode={catalogMode ? undefined : crud.handleDeleteNode}
                                    onAddRelationship={catalogMode ? undefined : (node) => {
                                        crud.setSelectedNode(node);
                                        crud.setShowRelationshipModal(true);
                                    }}
                                    onRatifyNode={catalogMode ? undefined : crud.handleRatifyNode}
                                    onUnratifyNode={catalogMode ? undefined : crud.handleUnratifyNode}
                                    onNavigate={(path) => navigate(path)}
                                    onSelectNode={async (n) => {
                                        if (n && !n.title && n.id) {
                                            try {
                                                const full = await api.getNode(n.id);
                                                crud.setSelectedNode(full.node || full);
                                            } catch {
                                                crud.setSelectedNode(n);
                                            }
                                        } else {
                                            crud.setSelectedNode(n);
                                        }
                                    }}
                                />
                        )}
                    </div>
                </div>
            )}


            {crud.showCreateModal && (
                <CreateNodeModal
                    onCreateNode={crud.handleCreateNode}
                    onCancel={() => crud.setShowCreateModal(false)}
                    availableSchemas={data.schemas}
                />
            )}

            {crud.showRelationshipModal && crud.selectedNode && (
                <AddRelationshipModal
                    sourceNode={crud.selectedNode}
                    onCancel={() => crud.setShowRelationshipModal(false)}
                    onSuccess={crud.handleAddRelationshipSuccess}
                />
            )}
        </div>
    );
}

export default GraphView;
